import { inject, Injectable, NgZone } from '@angular/core';

import { distinctUntilChanged } from 'rxjs';

import { Store } from '@ngrx/store';
import posthog from 'posthog-js';

import { selectUser } from '@app/auth/store/selectors/user.selectors';
import { environment } from '@app/environments/environment';
import { V1AuthUser } from '@app/models/api/v1/auth/auth.model';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private readonly ngZone = inject(NgZone);
  private readonly store = inject(Store);

  private started = false;
  private identifiedUserUuid: string | null = null;

  public init(): void {
    const posthogKey: string = environment.posthogKey;
    const posthogHost: string = environment.posthogHost;
    if (this.started || !posthogKey || !posthogHost) {
      return;
    }
    this.started = true;

    const apiHostname = this.backendHostname();
    this.ngZone.runOutsideAngular(() => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        defaults: '2026-05-30',
        ...(apiHostname ? { tracing_headers: [apiHostname] } : {}),
      });
    });

    this.store
      .select(selectUser)
      .pipe(
        distinctUntilChanged(
          (previous, current) =>
            previous?.uuid === current?.uuid && previous?.email === current?.email && previous?.full_name === current?.full_name,
        ),
      )
      .subscribe(user => {
        this.ngZone.runOutsideAngular(() => {
          this.syncIdentity(user);
        });
      });
  }

  private syncIdentity(user: V1AuthUser | null | undefined): void {
    if (!user?.uuid) {
      this.resetIdentity();
      return;
    }

    if (this.identifiedUserUuid && this.identifiedUserUuid !== user.uuid) {
      posthog.reset();
      this.identifiedUserUuid = null;
    }

    posthog.identify(user.uuid, {
      email: user.email,
      ...(user.full_name ? { name: user.full_name } : {}),
    });
    this.identifiedUserUuid = user.uuid;
  }

  private resetIdentity(): void {
    if (!this.identifiedUserUuid) {
      return;
    }
    posthog.reset();
    this.identifiedUserUuid = null;
  }

  private backendHostname(): string | undefined {
    if (!environment.backendUrl) {
      return undefined;
    }
    return new URL(environment.backendUrl).hostname;
  }
}
